import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import axios from "axios";
import { useStore } from "../context/StoreContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Light inline formatting: **bold** segments only.
const Rich = ({ text }) => (
  <>
    {text.split(/(\*\*[^*]+\*\*)/g).filter(Boolean).map((chunk, i) =>
      chunk.startsWith("**") && chunk.endsWith("**")
        ? <strong key={`${i}-b`} className="font-medium text-stone-900">{chunk.slice(2, -2)}</strong>
        : <span key={`${i}-t`}>{chunk}</span>
    )}
  </>
);

const Paragraphs = ({ body }) => (
  <div className="space-y-4">
    {body.split(/\n{2,}/).map((p) => (
      <p key={p.slice(0, 60)} className="text-base text-stone-600 leading-[1.8]"><Rich text={p} /></p>
    ))}
  </div>
);

const StorySection = ({ index, heading, body, image }) => {
  const flip = index % 2 === 1;
  return (
    <motion.section
      initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7 }}
      className="grid lg:grid-cols-12 gap-8 lg:gap-14 items-start" data-testid={`cms-section-${index}`}
    >
      {image && (
        <div className={`lg:col-span-5 ${flip ? "lg:order-2" : ""}`}>
          <div className="rounded-[1.25rem] overflow-hidden aspect-[4/3] bg-stone-100 lg:sticky lg:top-28">
            <img src={image} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
          </div>
        </div>
      )}
      <div className={image ? `lg:col-span-7 ${flip ? "lg:order-1" : ""}` : "lg:col-span-9 lg:col-start-2"}>
        <span className="font-mono text-[11px] tracking-[0.3em] text-[#C85A32]">{String(index + 1).padStart(2, "0")}</span>
        <h2 className="mt-3 mb-5 font-serif text-2xl lg:text-[1.75rem] leading-snug text-stone-900">{heading}</h2>
        <Paragraphs body={body} />
      </div>
    </motion.section>
  );
};

export default function CmsPage() {
  const { slug } = useParams();
  const { lang, t } = useStore();
  const [page, setPage] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setPage(null); setMissing(false);
    window.scrollTo(0, 0);
    axios.get(`${API}/pages/${slug}`).then((r) => setPage(r.data)).catch(() => setMissing(true));
  }, [slug]);

  const L = (obj) => (obj?.[lang] || obj?.fr || "");

  if (missing) return <div className="min-h-screen pt-40 text-center text-stone-500" data-testid="cms-page-missing">404</div>;
  if (!page) return <div className="min-h-screen pt-40 text-center text-stone-500 font-mono text-sm">…</div>;

  return (
    <div className="pt-32 pb-28 px-6 lg:px-14 xl:px-24 max-w-[1180px] mx-auto" data-testid={`cms-page-${slug}`}>
      <Link to="/" className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.2em] text-stone-500 hover:text-[#C85A32] transition-colors">
        <ArrowLeft size={13} /> {t.productPage.back}
      </Link>
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="mt-10 mb-20 max-w-3xl">
        <p className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#C85A32] mb-4">NŪRA</p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-[3.4rem] leading-[1.05] text-stone-900" data-testid="cms-page-title">{L(page.title)}</h1>
        {L(page.intro) && <p className="mt-7 text-base lg:text-lg text-stone-600 leading-[1.8] max-w-2xl"><Rich text={L(page.intro)} /></p>}
      </motion.div>
      <div className="space-y-24 lg:space-y-32">
        {page.sections.map((s, i) => (
          <StorySection key={s.id || i} index={i} heading={L(s.heading)} body={L(s.body)} image={s.image} />
        ))}
      </div>
    </div>
  );
}
